import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { MessageCircle, X, FlaskConical } from 'lucide-react';
import type { PurchaseFlow, ActivityEntry, PurchaseStage, ChatMessage } from '@/types/purchaseFlow';
import { STAGE_ORDER } from '@/types/purchaseFlow';
import { createFlow, getFlow, listFlows, loadFixture, updateFlowItems, updateFlowFields, addFlowItem, FIXTURE_LABELS } from '@/services/mockPurchaseFlowService';
import ChatPanel from './ChatPanel';
import FlowListPanel from './FlowListPanel';
import ContextPanel from './ContextPanel';

interface AgenticFlowDialogProps {
  isOpen: boolean;
  onClose: () => void;
  flowId?: string;
  flowButtonId?: string;
  /** Auto-load a fixture on open (e.g., '3_price_confirm') */
  initialFixture?: string;
}

const AgenticFlowDialog: React.FC<AgenticFlowDialogProps> = ({
  isOpen,
  onClose,
  flowId: initialFlowId,
  flowButtonId,
  initialFixture,
}) => {
  const [flow, setFlow] = useState<PurchaseFlow | null>(null);
  const [flows, setFlows] = useState<PurchaseFlow[]>([]);
  const [activeFlowId, setActiveFlowId] = useState<string | null>(initialFlowId || null);
  const [loading, setLoading] = useState(false);
  const [activityEntries, setActivityEntries] = useState<ActivityEntry[]>([]);
  const [preloadedMessages, setPreloadedMessages] = useState<ChatMessage[] | null>(null);
  const { t } = useTranslation('purchaseflow');
  const [, setSearchParams] = useSearchParams();

  // Sync URL querystring with current flow state
  const syncUrl = useCallback((flowId: string | null, stage?: string) => {
    if (!flowId) return;
    setSearchParams(prev => {
      prev.set('flow', flowId);
      if (stage) prev.set('stage', stage);
      else prev.delete('stage');
      return prev;
    }, { replace: true });
  }, [setSearchParams]);

  // Load a fixture for UI preview
  const handleLoadFixture = useCallback((fixtureKey: string) => {
    const fixture = loadFixture(fixtureKey);
    if (!fixture) return;
    setFlow({ ...fixture.flow });
    setActiveFlowId(fixture.flow.flow_id);
    setActivityEntries([...fixture.activity]);
    setPreloadedMessages([...fixture.messages]);
    syncUrl(fixture.flow.flow_id, fixture.flow.current_stage);
    listFlows().then(r => setFlows(r.flows));
  }, [syncUrl]);

  // Initialize: create or load flow (or load fixture from ?flow= param)
  useEffect(() => {
    if (!isOpen) return;

    // If initialFixture is provided, load it directly
    if (initialFixture) {
      handleLoadFixture(initialFixture);
      return;
    }

    const init = async () => {
      setLoading(true);
      try {
        let currentFlow: PurchaseFlow;
        if (initialFlowId) {
          currentFlow = await getFlow(initialFlowId);
        } else {
          currentFlow = await createFlow(flowButtonId);
        }
        setFlow(currentFlow);
        setActiveFlowId(currentFlow.flow_id);
        syncUrl(currentFlow.flow_id, currentFlow.current_stage);

        // Load other flows for the sidebar
        const result = await listFlows();
        setFlows(result.flows);
      } catch (err) {
        console.error('Failed to init flow:', err);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [isOpen, initialFlowId, flowButtonId, initialFixture, handleLoadFixture, syncUrl]);

  // Switch flow
  const handleSelectFlow = useCallback(async (flowId: string) => {
    try {
      const f = await getFlow(flowId);
      setFlow(f);
      setActiveFlowId(flowId);
      setActivityEntries([]);
      syncUrl(flowId, f.current_stage);
    } catch (err) {
      console.error('Failed to switch flow:', err);
    }
  }, [syncUrl]);

  // Create new flow from sidebar
  const handleCreateNewFlow = useCallback(async () => {
    try {
      const newFlow = await createFlow(flowButtonId);
      setFlow(newFlow);
      setActiveFlowId(newFlow.flow_id);
      setActivityEntries([]);
      syncUrl(newFlow.flow_id, newFlow.current_stage);
      const result = await listFlows();
      setFlows(result.flows);
    } catch (err) {
      console.error('Failed to create new flow:', err);
    }
  }, [flowButtonId, syncUrl]);

  // Refresh flow state after agent turn
  const handleFlowUpdated = useCallback(async () => {
    if (!activeFlowId) return;
    try {
      const f = await getFlow(activeFlowId);
      setFlow(f);
      syncUrl(activeFlowId, f.current_stage);
      const result = await listFlows();
      setFlows(result.flows);
    } catch {
      // Silently ignore refresh errors
    }
  }, [activeFlowId, syncUrl]);

  // Tool calls from ChatPanel → ContextPanel activity tab
  const handleToolCallsUpdated = useCallback((entries: ActivityEntry[]) => {
    setActivityEntries(entries);
  }, []);

  // Direct flow item updates from PriceTierTableBlock → right panel draft
  const handleUpdateItems = useCallback(
    (updates: { part_code: string; quantity: number; unit_price: number }[], removals: string[]) => {
      console.log('[AgenticFlowDialog] handleUpdateItems called', { activeFlowId, updates, removals });
      if (!activeFlowId) return;
      updateFlowItems(activeFlowId, updates, removals);
      // Refresh flow state so ContextPanel re-renders
      getFlow(activeFlowId).then(f => {
        console.log('[AgenticFlowDialog] flow refreshed, items:', (f.stage_data as Record<string, unknown>)?.items);
        setFlow(f);
      }).catch(() => {});
    },
    [activeFlowId],
  );

  // Direct flow field updates from DeliveryDateBlock, DeliveryAddressBlock, PaymentTermsBlock
  const handleUpdateFlowFields = useCallback(
    (fields: Record<string, unknown>) => {
      if (!activeFlowId) return;
      updateFlowFields(activeFlowId, fields);
      getFlow(activeFlowId).then(f => setFlow(f)).catch(() => {});
    },
    [activeFlowId],
  );

  // Add item directly from ProductListBlock → right panel draft
  const handleAddItem = useCallback(
    (item: { part_code: string; product_name: string; specification: string; quantity: number; unit_price: number; currency: string }) => {
      if (!activeFlowId) return;
      addFlowItem(activeFlowId, item);
      getFlow(activeFlowId).then(f => setFlow(f)).catch(() => {});
    },
    [activeFlowId],
  );

  // Escape to close
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  // Lock body scroll
  useEffect(() => {
    if (isOpen) document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  if (!isOpen) return null;

  const stageLabel = flow?.current_stage
    ? t(`stages.${flow.current_stage}`)
    : '';

  const stageNumber = flow
    ? STAGE_ORDER.indexOf(flow.current_stage as PurchaseStage) + 1
    : 0;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 select-text">
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-2xl w-full mx-4 sm:mx-6 flex flex-col"
        style={{ height: '90vh', maxHeight: '90vh' }}>

        {/* Header */}
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-4 sm:px-6 py-3 flex items-center justify-between flex-shrink-0 rounded-t-lg">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
              <MessageCircle className="w-4 h-4 text-white" />
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-bold text-white truncate">
                {t('titleZh')} — {t('title')}
              </h2>
              {flow?.po_id && (
                <p className="text-white/80 text-xs truncate">
                  {(flow.stage_data as Record<string, unknown>)?.po_number as string || flow.po_id}
                </p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body: 3-panel grid (2 / 5 / 5) */}
        <div className="flex-1 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-full text-gray-500">
              <div className="animate-spin w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full" />
              <span className="ml-3">{t('initializingFlow')}</span>
            </div>
          ) : (
            <div className="grid grid-cols-12 h-full">
              {/* Left: Flow List (hidden below xl) */}
              <FlowListPanel
                className="col-span-2 hidden xl:flex"
                flows={flows}
                activeFlowId={activeFlowId}
                onSelectFlow={handleSelectFlow}
                onCreateNew={handleCreateNewFlow}
              />

              {/* Center: Chat */}
              <ChatPanel
                className="col-span-12 xl:col-span-5 lg:col-span-7"
                flow={flow}
                onFlowUpdated={handleFlowUpdated}
                onToolCallsUpdated={handleToolCallsUpdated}
                onUpdateItems={handleUpdateItems}
                onUpdateFlowFields={handleUpdateFlowFields}
                onAddItem={handleAddItem}
                preloadedMessages={preloadedMessages}
              />

              {/* Right: Context (hidden below lg) */}
              <ContextPanel
                className="col-span-5 hidden lg:flex"
                flow={flow}
                activityEntries={activityEntries}
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 dark:bg-gray-800 px-4 sm:px-6 py-2 flex items-center justify-between flex-shrink-0 rounded-b-lg border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
            <span className={`w-2 h-2 rounded-full transition-colors duration-300 ${
              flow?.status === 'active' ? 'bg-blue-500' :
              flow?.status === 'paused' ? 'bg-amber-500' :
              flow?.status === 'completed' ? 'bg-emerald-500' : 'bg-gray-400'
            }`} />
            <span
              key={`stage-${stageNumber}-${flow?.current_stage}`}
              className="animate-stage-flash"
            >
              {t('stageOf', { number: stageNumber, label: stageLabel })}
            </span>
            {flow?.status === 'completed' && (
              <span className="text-emerald-500 font-medium ml-1">✓ {t('completed')}</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {/* Fixture preview selector (dev only) */}
            <div className="flex items-center gap-1.5">
              <FlaskConical className="w-3.5 h-3.5 text-gray-400" />
              <select
                onChange={(e) => { if (e.target.value) handleLoadFixture(e.target.value); }}
                value=""
                className="text-[10px] bg-transparent border border-gray-300 dark:border-gray-600 rounded px-1.5 py-0.5 text-gray-500 dark:text-gray-400 cursor-pointer hover:border-blue-400 focus:ring-1 focus:ring-blue-500"
              >
                <option value="">Preview stage...</option>
                {Object.entries(FIXTURE_LABELS).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>
            <button
              onClick={onClose}
              className="text-xs text-red-600 hover:text-red-700 dark:text-red-400 px-3 py-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
            >
              {t('close')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgenticFlowDialog;
